import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Status } from '../content';

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// The badge, drawn inline (never recoloured). size in px.
export function Badge({ size = 32, className }: { size?: number; className?: string }) {
  const id = React.useId().replace(/:/g, '');
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={`g${id}`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#3FAE6C" />
          <stop offset=".55" stopColor="#8FD53A" />
          <stop offset="1" stopColor="#CFF710" />
        </linearGradient>
      </defs>
      <rect x="5" y="5" width="90" height="90" rx="26" fill={`url(#g${id})`} />
      <rect x="13" y="13" width="74" height="74" rx="19" fill="#fff" />
      <text x="50" y="71" textAnchor="middle" fontFamily="Poppins,Arial,sans-serif" fontWeight="800" fontSize="52" fill="#241E61">
        B
      </text>
    </svg>
  );
}

// Lockup: badge + stacked wordmark. reversed = white wordmark for dark grounds.
export function Logo({ reversed = false, size = 34 }: { reversed?: boolean; size?: number }) {
  const color = reversed ? '#fff' : '#241E61';
  return (
    <span className="inline-flex items-center gap-2.5" aria-label="BrandedMark">
      <Badge size={size} />
      <span style={{ color, fontWeight: 800, lineHeight: 0.92, fontSize: size * 0.43, letterSpacing: '-0.02em' }}>
        Branded
        <br />
        Mark
      </span>
    </span>
  );
}

// Gradient underline on one key phrase. draw = animate in on mount.
export function Swipe({ children, draw = false }: { children: React.ReactNode; draw?: boolean }) {
  return <span className={cx('swipe', draw && 'swipe-draw')}>{children}</span>;
}

// Internal links use the router; /blog, /trust, /legal etc. are static pages and need a full load.
const STATIC_PREFIXES = ['/blog', '/trust', '/legal', '/tools', '/for/', '/see-a-demo', '/.well-known', '/start'];
export const isStaticHref = (href: string) => /^(https?:|mailto:|tel:)/.test(href) || STATIC_PREFIXES.some((p) => href.startsWith(p));

export function SmartLink({ href, className, children, ...rest }: { href: string; className?: string; children: React.ReactNode } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  if (isStaticHref(href)) {
    return (
      <a href={href} className={className} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} {...rest}>
      {children}
    </Link>
  );
}

type BtnKind = 'primary' | 'go' | 'ghost';
export function Button({ href, kind = 'primary', small, children, className, onClick }: { href?: string; kind?: BtnKind; small?: boolean; children: React.ReactNode; className?: string; onClick?: () => void }) {
  const cls = cx('btn', `btn-${kind}`, small && 'btn-sm', className);
  if (href) return <SmartLink href={href} className={cls} onClick={onClick}>{children}</SmartLink>;
  return (
    <button type="button" className={cls} onClick={onClick}>
      {children}
    </button>
  );
}

export const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8h9.5M8.5 3.5 13 8l-4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function Eyebrow({ children, bar = false }: { children: React.ReactNode; bar?: boolean }) {
  return (
    <div>
      {bar && <span className="bar" />}
      <div className="t-eyebrow">{children}</div>
    </div>
  );
}

// Status labels are hidden on the public site (user, 20 Oct): "In pilot" reads as unfinished. The status data stays,
// so copy can never claim a pilot feature as live; StatusChip renders nothing.
export function StatusChip(_: { status: Status }) {
  return null;
}

// Adds .is-in to [data-reveal] descendants as they scroll into view.
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const reveal = (e: Element) => e.classList.add('is-in');
    if (!('IntersectionObserver' in window)) {
      root.querySelectorAll('[data-reveal]').forEach(reveal);
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            reveal(e.target);
            io.unobserve(e.target);
          }
        }),
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );
    const watch = () => {
      const els = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]:not(.is-in)'));
      if (root.hasAttribute('data-reveal') && !root.classList.contains('is-in')) els.push(root);
      els.forEach((e) => io.observe(e));
    };
    watch();
    // Content can appear later (tabs, currency toggles, async data): watch for it too.
    const mo = new MutationObserver(watch);
    mo.observe(root, { childList: true, subtree: true });
    // Only now may [data-reveal] content start hidden (see v2.css); without this hook it is always visible.
    root.classList.add('reveal-ready');
    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
  return ref;
}

// True once the element has been on screen (for starting animations).
export function useInView<T extends HTMLElement>(threshold = 0.3) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView] as const;
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// A phone frame for product UI mockups.
export function Phone({ children, className, dark = false }: { children: React.ReactNode; className?: string; dark?: boolean }) {
  return (
    <div
      className={cx('relative mx-auto', className)}
      style={{
        width: 280,
        aspectRatio: '280 / 580',
        borderRadius: 44,
        padding: 10,
        background: dark ? '#0f0c2e' : '#181343',
        boxShadow: '0 40px 80px -30px rgba(24,19,67,.55), inset 0 0 0 1.5px rgba(255,255,255,.08)',
      }}
    >
      <div className="relative h-full w-full overflow-hidden bg-white" style={{ borderRadius: 34 }}>
        <div className="absolute inset-x-0 top-2 z-10 mx-auto h-[22px] w-[86px] rounded-full bg-[#181343]" />
        {children}
      </div>
    </div>
  );
}

// A browser/app window for admin mockups.
export function Window({ children, className, title = 'admin.brandedmark.com' }: { children: React.ReactNode; className?: string; title?: string }) {
  return (
    <div className={cx('overflow-hidden rounded-2xl bg-white', className)} style={{ boxShadow: '0 30px 70px -30px rgba(24,19,67,.35), 0 0 0 1px #E4E3F0' }}>
      <div className="flex items-center gap-2 border-b border-[#E4E3F0] bg-[#F7F7FC] px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#E4E3F0]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#E4E3F0]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#E4E3F0]" />
        <span className="ml-3 font-mono text-[11px] text-[#67648F]">{title}</span>
      </div>
      {children}
    </div>
  );
}

// Sets document title and meta description per page.
export function usePageMeta(title: string, description?: string) {
  useEffect(() => {
    document.title = title.includes('BrandedMark') ? title : `${title} | BrandedMark`;
    if (description) {
      let m = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (!m) {
        m = document.createElement('meta');
        m.name = 'description';
        document.head.appendChild(m);
      }
      m.content = description;
    }
  }, [title, description]);
}
